import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl, brands } from "../../config/config";
import SEO from "../SEO";
import ProductGridSkeleton from "./ProductGridSkeleton";

const ProductGrid = ({ categoryId, categoryName }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const formattedName = categoryName
    ? categoryName.replace(/-/g, " ")
    : "Products";

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);

      let url = "";
      let applySlice = true;

      if (categoryId === "all") {
        url = `${apiUrl}/api/${brands}/products`;
      } else if (categoryId === "allproducts") {
        url = `${apiUrl}/api/${brands}/products`;
        applySlice = false;
      } else {
        url = `${apiUrl}/api/${brands}/products/category/${categoryId}`;
      }

      try {
        const res = await fetch(url);
        const data = await res.json();

        let productsList = [];

        if (Array.isArray(data)) {
          productsList = applySlice ? data.slice(0, 8) : data;
        } else if (data.data && Array.isArray(data.data)) {
          productsList = applySlice ? data.data.slice(0, 8) : data.data;
        }

        setProducts(productsList);
      } catch (err) {
        console.error("Product fetch error:", err);
        setProducts([]);
      }

      setLoading(false);
    };

    fetchProducts();
  }, [categoryId]);

  const category = products?.[0]?.category;

  if (loading) {
    return <ProductGridSkeleton count={8} />;
  }

  if (products.length === 0) {
    return (
      <div className="w-full bg-lightPastelPurple px-4 py-12">
        <p className="text-center text-base font-semibold text-[#5f5870]">
          No products found.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* <SEO
        title={
          category?.meta_title ||
          `${category?.category_name || "Products"} | Your Brand`
        }
        description={
          category?.meta_description ||
          `Browse ${category?.category_name || "our"} products at best prices`
        }
        keywords={
          category?.meta_keywords ||
          category?.category_name ||
          "custom patches"
        }
      /> */}

      <section className="relative w-full overflow-hidden bg-lightPastelPurple px-4 py-10 md:py-12">
        <div className="absolute left-0 top-8 h-56 w-56 rounded-full bg-pastelPurple opacity-60 blur-3xl"></div>
        <div className="absolute bottom-8 right-0 h-56 w-56 rounded-full bg-keyLime/20 blur-3xl"></div>

        <div className="relative mx-auto max-w-7xl">
          {/* HEADING */}
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-darkPurple md:text-4xl">
              {formattedName}
            </h2>

            <div className="mx-auto mt-3 flex w-full max-w-[220px] items-center justify-center gap-3">
              <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
              <span className="h-2.5 w-2.5 rounded-full bg-keyLime shadow-[0_0_0_5px_rgba(215,255,79,0.25)]"></span>
              <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
            </div>
          </div>

          {/* PRODUCT GRID */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
            {products.map((item, index) => (
              <Link
                key={`${categoryId}-${item.id}`}
                to={`/product/${item.category.category_name.replace(/\s+/g, "-")}/${item.category_id}/${item.name.replace(/\s+/g, "-")}/${item.id}`}
                className="group block overflow-hidden rounded-[22px] border border-pastelPurple bg-white p-3 opacity-0 shadow-[0_10px_26px_rgba(47,34,84,0.07)] animate-fadeFromTop transition-all duration-300 hover:-translate-y-1 hover:border-[#d8c8ff] hover:shadow-[0_18px_42px_rgba(47,34,84,0.13)]"
                style={{ animationDelay: `${index * 120}ms` }}
              >
                {/* IMAGE */}
                <div className="flex h-[190px] items-center justify-center overflow-hidden rounded-[18px] bg-gradient-to-br from-[#f2ecff] via-white to-[#f8ffe0] p-3 md:h-[205px]">
                  <img
                    src={`${apiUrl}/storage/${item.image}`}
                    alt={item.name}
                    className="max-h-full max-w-full rounded-xl object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                {/* TITLE */}
                <div className="px-1 pb-1 pt-4 text-center">
                  <h3 className="min-h-[48px] text-base font-extrabold leading-6 text-darkPurple md:text-[17px]">
                    {item.name}
                  </h3>

                  <p className="mt-1.5 text-sm font-bold text-themePurple">
                    As low as{" "}
                    <span className="text-base font-extrabold text-[#5f7f00]">
                      ${item.basePrice}
                    </span>
                  </p>
                  <div className="mx-auto mt-4 inline-flex rounded-xl bg-keyLime px-5 py-2 text-sm font-extrabold text-darkPurple transition-all duration-300 group-hover:bg-[#c8f531]">
                    View Product
                  </div>
                </div>
              </Link>
            ))}
          </div>  
          {/* PREMIUM CONTENT SECTION */}
          <div className="mt-10 rounded-[24px] border border-pastelPurple bg-white p-5 shadow-[0_12px_30px_rgba(47,34,84,0.08)] md:p-7 lg:p-8">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-3xl">
                Premium shirts patches for Style, Identity & Branding
              </h2>
            
              <div className="mx-auto mt-3 flex w-full max-w-[210px] items-center justify-center gap-3">
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-keyLime shadow-[0_0_0_5px_rgba(215,255,79,0.25)]"></span>
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
              </div>

              <p className="mt-5 text-base font-medium leading-7 text-[#5f5870] md:text-lg md:leading-8">
                Looking for high-quality shirts patches that upgrade your clothing instantly? Our premium patch solutions help you express your style, promote your brand, and stand out with confidence.
              </p>

              <p className="mt-3 text-base font-medium leading-7 text-[#5f5870] md:text-lg md:leading-8">
                We create durable and stylish custom patches for shirts that fit perfectly on all fabrics. Whether you want bold branding or simple fashion upgrades, our shirts patches are designed to last and look great every day.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductGrid;